import { Button, Input, Modal, RichTextEditor, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { apiBase, webTokenStorage } from '../../lib/api.ts'
import {
  TIPOS_CAMPO,
  useCatalog,
  useDeleteGroup,
  useSaveField,
  useSaveGroup,
  type CustomField,
  type CustomFieldGroup,
} from './custom-field-data.ts'

/**
 * Painel de catálogo de campos (SPEC-20260828-1914) — componente reutilizável.
 *
 * Lista todos os campos do workspace, agrupados por entidade (Item / Empresa /
 * Pessoa), com busca. Cada campo é arrastável: quem consome define o alvo (uma
 * aba, a barra de propriedades ou, no futuro, um formulário). O tipo MIME
 * `DRAG_MIME` carrega `{ fieldId, entity }`.
 *
 * Dentro de cada entidade cabem PASTAS de um nível (pedido 2026-08-30), que
 * recolhem e expandem. A pasta é só a organização da estante — onde o campo
 * APARECE continua sendo decidido pela colocação num tópico.
 */

export const DRAG_MIME = 'application/x-orbind-field'

const rotuloDe = (labels: Record<string, string>, fallback: string) =>
  labels['pt-BR'] || Object.values(labels)[0] || fallback

type Entidade = 'work_item' | 'company' | 'person'

const GRUPOS: { entity: Entidade; titulo: string; icon: string }[] = [
  { entity: 'work_item', titulo: 'Campos de Item de Trabalho', icon: 'fas fa-tasks' },
  { entity: 'company', titulo: 'Campos de Empresa', icon: 'fas fa-building' },
  { entity: 'person', titulo: 'Campos de Pessoa', icon: 'fas fa-user' },
]

export function CatalogoCampos({
  onNovoCampo,
  onNovoCampoDoTipo,
  onEditarCampo,
  onArrastarInicio,
  onArrastarFim,
  onAbrirLista,
}: {
  /** Novo campo na entidade; `folderId` já nasce dentro da pasta. */
  onNovoCampo?: (entity: Entidade, folderId?: string | null) => void
  /** "Criar campo" do rodapé: já abre a tela do TIPO escolhido (2026-08-30). */
  onNovoCampoDoTipo?: (tipo: CustomField['type']) => void
  onEditarCampo?: (campo: CustomField) => void
  onArrastarInicio?: (entity: string) => void
  onArrastarFim?: () => void
  /** Abre a lista completa de campos na área de trabalho. */
  onAbrirLista?: () => void
}) {
  const catalogo = useCatalog()
  const toast = useToast()
  const qcCat = useQueryClient()
  const salvarCampo = useSaveField('catalog')
  const salvarPasta = useSaveGroup('catalog')
  const apagarPasta = useDeleteGroup('catalog')

  const [busca, setBusca] = useState('')
  const [recolhidos, setRecolhidos] = useState<ReadonlySet<string>>(new Set())
  /** Acordeão "Criar campo" do rodapé, com os tipos em cards. */
  const [criarAberto, setCriarAberto] = useState(false)
  /** Entidade com o campo de "nova pasta" aberto. */
  const [novaPastaEm, setNovaPastaEm] = useState<Entidade | null>(null)
  const [nomeNovaPasta, setNomeNovaPasta] = useState('')
  /** Pasta cujo nome está sendo editado. */
  const [renomeando, setRenomeando] = useState<{ id: string; nome: string } | null>(null)
  /**
   * Campos de SISTEMA no catalogo (2026-09-01): nao podem ser apagados; so a
   * INSTRUCAO de preenchimento (dado sem impacto no funcionamento) e editavel
   * — ela vira o padrao do "i" nos itens.
   */
  const sistema = useQuery({
    queryKey: ['system-field-help'],
    queryFn: async () => {
      const r = await fetch(`${apiBase()}/custom-fields/system`, {
        headers: { authorization: `Bearer ${webTokenStorage.get() ?? ''}` },
      })
      const d = (await r.json()) as {
        systemFields: { key: string; nome: string; icone: string; help: string; editado: boolean }[]
      }
      return d.systemFields
    },
  })
  const [sistemaAberto, setSistemaAberto] = useState<string | null>(null)
  const [sistemaEditando, setSistemaEditando] = useState(false)
  const salvarSistema = useMutation({
    mutationFn: async (input: { key: string; html: string }) => {
      const r = await fetch(`${apiBase()}/custom-fields/system/${encodeURIComponent(input.key)}`, {
        method: 'PUT',
        headers: {
          authorization: `Bearer ${webTokenStorage.get() ?? ''}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ html: input.html }),
      })
      if (!r.ok) throw new Error('Falha ao salvar as instruções.')
      return r.json()
    },
    onSuccess: () => {
      qcCat.invalidateQueries({ queryKey: ['system-field-help'] })
      setSistemaEditando(false)
      toast.push({ tone: 'success', message: 'Instruções salvas.' })
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar.' }),
  })
  /**
   * O realce de "vai cair aqui" é escrito direto no DOM, fora do estado.
   *
   * Com estado, o `dragleave` que sobe dos elementos de dentro apagava o realce
   * que o `dragover` tinha acabado de acender — liga/desliga a cada movimento do
   * cursor, que é a piscadeira do painel. Também não há `dragleave` nenhum: sair
   * de um alvo é entrar em outro, e o fim do arraste limpa tudo.
   */
  const painelRef = useRef<HTMLElement>(null)
  const alvoAtual = useRef<string | null>(null)
  /** Alvo da REORDENACAO: sobre qual campo o traco esta, e de que lado. */
  const ordemAlvo = useRef<{ id: string; antes: boolean } | null>(null)

  const realcar = (el: HTMLElement | null, id: string | null) => {
    if (alvoAtual.current === id) return
    alvoAtual.current = id
    for (const n of painelRef.current?.querySelectorAll('.cat__pasta--sobre, .cat__grupo--sobre') ?? []) {
      n.classList.remove('cat__pasta--sobre', 'cat__grupo--sobre')
    }
    if (el) el.classList.add(el.classList.contains('cat__pasta') ? 'cat__pasta--sobre' : 'cat__grupo--sobre')
  }
  const limparRealce = () => realcar(null, null)

  /** Traco de "entra aqui" entre os campos — escrito direto no DOM. */
  const marcarOrdem = (el: HTMLElement | null, id: string | null, antes = true) => {
    if (ordemAlvo.current?.id === id && ordemAlvo.current?.antes === antes) return
    ordemAlvo.current = id ? { id, antes } : null
    for (const nEl of painelRef.current?.querySelectorAll('.cat__campo--antes, .cat__campo--depois') ?? []) {
      nEl.classList.remove('cat__campo--antes', 'cat__campo--depois')
    }
    if (el) el.classList.add(antes ? 'cat__campo--antes' : 'cat__campo--depois')
  }

  const alternar = (id: string) =>
    setRecolhidos((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  const porEntidade = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    const filtra = (xs: CustomField[]) =>
      termo === ''
        ? xs
        : xs.filter((f) => rotuloDe(f.labels, f.key).toLowerCase().includes(termo) || f.key.includes(termo))
    return {
      work_item: filtra(catalogo.data?.work_item ?? []),
      company: filtra(catalogo.data?.company ?? []),
      person: filtra(catalogo.data?.person ?? []),
    }
  }, [catalogo.data, busca])

  const pastas = catalogo.data?.folders ?? []

  const arrastar = (f: CustomField) => (e: React.DragEvent) => {
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ fieldId: f.id, entity: f.entity }))
    e.dataTransfer.setData('text/plain', rotuloDe(f.labels, f.key))
    // 'copyMove': soltar numa aba COPIA (cria colocação); soltar numa pasta MOVE
    e.dataTransfer.effectAllowed = 'copyMove'
    onArrastarInicio?.(f.entity)
  }

  /** Move o campo para uma pasta (ou para fora, com `folderId` nulo). */
  const soltarNaPasta = (entity: Entidade, folderId: string | null) => (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    limparRealce()
    const bruto = e.dataTransfer.getData(DRAG_MIME)
    if (!bruto) return
    let dado: { fieldId: string; entity: string }
    try {
      dado = JSON.parse(bruto)
    } catch {
      return
    }
    if (dado.entity !== entity) {
      toast.push({ tone: 'warning', message: 'A pasta é de outra entidade.' })
      return
    }
    const campo = [...(catalogo.data?.work_item ?? []), ...(catalogo.data?.company ?? []), ...(catalogo.data?.person ?? [])].find(
      (f) => f.id === dado.fieldId,
    )
    if (!campo || (campo.catalogGroupId ?? null) === folderId) return
    salvarCampo.mutate(
      { id: dado.fieldId, catalogGroupId: folderId },
      {
        onSuccess: () => toast.push({ tone: 'success', message: folderId ? 'Campo movido.' : 'Campo tirado da pasta.' }),
        onError: (err) => toast.push({ tone: 'danger', message: err instanceof Error ? err.message : 'Falha ao mover.' }),
      },
    )
  }

  /**
   * Solta um campo SOBRE outro: entra exatamente onde o traço apareceu
   * (2026-08-31 — antes só dava para mover ENTRE pastas, não ordenar dentro).
   * Se o alvo está noutra pasta, o campo é adotado por ela junto.
   */
  const soltarNoCampo = (alvo: CustomField) => (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const antes = ordemAlvo.current?.antes ?? true
    limparRealce()
    marcarOrdem(null, null)
    const bruto = e.dataTransfer.getData(DRAG_MIME)
    if (!bruto) return
    let dado: { fieldId: string; entity: string }
    try {
      dado = JSON.parse(bruto)
    } catch {
      return
    }
    if (dado.fieldId === alvo.id) return
    if (dado.entity !== alvo.entity) {
      toast.push({ tone: 'warning', message: 'A ordem é por entidade — solte entre campos da mesma.' })
      return
    }
    const todos = [...(catalogo.data?.[alvo.entity as Entidade] ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
    const origem = todos.find((f) => f.id === dado.fieldId)
    if (!origem) return
    const sem = todos.filter((f) => f.id !== origem.id)
    const at = sem.findIndex((f) => f.id === alvo.id)
    if (at < 0) return
    sem.splice(antes ? at : at + 1, 0, origem)
    const mudados = sem.map((f, i) => ({ f, i })).filter(({ f, i }) => f.sortOrder !== i)
    const pastaDoAlvo = alvo.catalogGroupId ?? null
    const mudaPasta = (origem.catalogGroupId ?? null) !== pastaDoAlvo
    if (mudados.length === 0 && !mudaPasta) return
    Promise.all([
      ...(mudaPasta ? [salvarCampo.mutateAsync({ id: origem.id, catalogGroupId: pastaDoAlvo })] : []),
      ...mudados.map(({ f, i }) => salvarCampo.mutateAsync({ id: f.id, sortOrder: i })),
    ])
      .then(() => toast.push({ tone: 'success', message: 'Ordem salva.' }))
      .catch((err) => toast.push({ tone: 'danger', message: err instanceof Error ? err.message : 'Falha ao reordenar.' }))
  }

  /**
   * Handlers de um alvo de soltar. `parar` é para a PASTA: ela vive dentro da
   * área da entidade, e sem cortar a propagação o realce saltaria da pasta para
   * a entidade inteira a cada movimento.
   */
  const aceitaCampo = (id: string, parar = false) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_MIME)) return
      if (parar) e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      realcar(e.currentTarget as HTMLElement, id)
    },
  })

  const criarPasta = (entity: Entidade) => {
    const nome = nomeNovaPasta.trim()
    if (!nome) return
    salvarPasta.mutate(
      { entity, kind: 'catalog', labels: { 'pt-BR': nome } } as never,
      {
        onSuccess: () => {
          setNovaPastaEm(null)
          setNomeNovaPasta('')
          toast.push({ tone: 'success', message: 'Pasta criada.' })
        },
        onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao criar a pasta.' }),
      },
    )
  }

  const renomear = () => {
    if (!renomeando) return
    const nome = renomeando.nome.trim()
    if (!nome) return setRenomeando(null)
    salvarPasta.mutate({ id: renomeando.id, labels: { 'pt-BR': nome } } as never, {
      onSuccess: () => setRenomeando(null),
      onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao renomear.' }),
    })
  }

  /** Um campo do catálogo (arrastável). */
  const cartao = (f: CustomField) => (
    <div
      key={f.id}
      className="cat__campo"
      draggable
      onDragStart={arrastar(f)}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DRAG_MIME)) return
        e.preventDefault()
        e.stopPropagation()
        e.dataTransfer.dropEffect = 'move'
        // metade de cima = entra ANTES; metade de baixo = DEPOIS
        const r = e.currentTarget.getBoundingClientRect()
        limparRealce()
        marcarOrdem(e.currentTarget as HTMLElement, f.id, e.clientY < r.top + r.height / 2)
      }}
      onDrop={soltarNoCampo(f)}
      onDragEnd={() => {
        limparRealce()
        marcarOrdem(null, null)
        onArrastarFim?.()
      }}
      onDoubleClick={() => onEditarCampo?.(f)}
      title="Arraste para uma aba ou para uma pasta · duplo-clique para editar"
    >
      <i className="fas fa-grip-vertical cat__grip" aria-hidden />
      <span className="cat__campo-nome">{rotuloDe(f.labels, f.key)}</span>
      <span className="cat__campo-tipo">{TIPOS_CAMPO.find((x) => x.value === f.type)?.label ?? f.type}</span>
    </div>
  )

  /** Uma pasta com seus campos. */
  const pasta = (p: CustomFieldGroup, entity: Entidade, dentro: CustomField[]) => {
    const chave = `pasta:${p.id}`
    const aberta = busca.trim() !== '' || !recolhidos.has(chave)
    const editando = renomeando?.id === p.id
    return (
      <div
        key={p.id}
        className="cat__pasta"
        onDrop={soltarNaPasta(entity, p.id)}
        {...aceitaCampo(chave, true)}
      >
        <div className="cat__pasta-cab">
          <button type="button" className="cat__pasta-tog" aria-expanded={aberta} onClick={() => alternar(chave)}>
            <i className={`fas fa-chevron-${aberta ? 'down' : 'right'} cat__grupo-chev`} aria-hidden />
            <i className={`fas fa-folder${aberta ? '-open' : ''} cat__pasta-ico`} aria-hidden />
          </button>
          {editando ? (
            <input
              className="cat__pasta-nome-edit"
              value={renomeando.nome}
              autoFocus
              onChange={(e) => setRenomeando({ id: p.id, nome: e.target.value })}
              onBlur={renomear}
              onKeyDown={(e) => {
                if (e.key === 'Enter') renomear()
                if (e.key === 'Escape') setRenomeando(null)
              }}
            />
          ) : (
            <button
              type="button"
              className="cat__pasta-nome"
              title="Renomear pasta"
              onDoubleClick={() => setRenomeando({ id: p.id, nome: rotuloDe(p.labels, 'Pasta') })}
              onClick={() => alternar(chave)}
            >
              {rotuloDe(p.labels, 'Pasta')}
            </button>
          )}
          {onNovoCampo ? (
            <button
              type="button"
              className="cat__grupo-add"
              title="Novo campo nesta pasta"
              aria-label="Novo campo nesta pasta"
              onClick={() => onNovoCampo(entity, p.id)}
            >
              <i className="fas fa-plus" aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            className="cat__pasta-del"
            title="Excluir pasta (os campos voltam para fora)"
            aria-label="Excluir pasta"
            onClick={() => apagarPasta.mutate(p.id)}
          >
            <i className="fas fa-trash" aria-hidden />
          </button>
          <span className="cat__grupo-n">{dentro.length}</span>
        </div>
        {aberta ? (
          dentro.length === 0 ? (
            <p className="cat__vazio">Arraste campos para cá, ou use o +.</p>
          ) : (
            <div className="cat__lista cat__lista--pasta">{dentro.map(cartao)}</div>
          )
        ) : null}
      </div>
    )
  }

  return (
    <aside ref={painelRef} className="cat" aria-label="Catálogo de campos">
      <div className="cat__topo">
        <h3 className="cat__titulo">Catálogo de campos</h3>
        <p className="cat__ajuda">Crie um campo (+) e arraste para uma aba, para a barra ou para uma pasta.</p>
        <Input
          value={busca}
          placeholder="Buscar campo…"
          aria-label="Buscar campo"
          onChange={(e) => setBusca(e.target.value)}
        />
        {onAbrirLista ? (
          <button type="button" className="cat__ver-todos" onClick={onAbrirLista}>
            <i className="fas fa-list" aria-hidden />
            Ver todos os campos
          </button>
        ) : null}
      </div>

      <div className="cat__grupos">
        {GRUPOS.map((g) => {
          const lista = porEntidade[g.entity]
          // com busca ativa, expande tudo para o resultado aparecer
          const aberto = busca.trim() !== '' || !recolhidos.has(g.entity)
          const daEntidade = pastas.filter((p) => p.entity === g.entity)
          const soltos = lista.filter((f) => !f.catalogGroupId)
          return (
            <div
              key={g.entity}
              className="cat__grupo"
              // soltar fora de qualquer pasta tira o campo da pasta
              onDrop={soltarNaPasta(g.entity, null)}
              {...aceitaCampo(`raiz:${g.entity}`)}
            >
              <div className="cat__grupo-cab">
                <button
                  type="button"
                  className="cat__grupo-tog"
                  aria-expanded={aberto}
                  onClick={() => alternar(g.entity)}
                >
                  <i className={`fas fa-chevron-${aberto ? 'down' : 'right'} cat__grupo-chev`} aria-hidden />
                  <i className={`${g.icon} cat__grupo-ico`} aria-hidden />
                  <span className="cat__grupo-tit">{g.titulo}</span>
                </button>
                <button
                  type="button"
                  className="cat__grupo-add"
                  title={`Nova pasta em ${g.titulo}`}
                  aria-label={`Nova pasta em ${g.titulo}`}
                  onClick={() => {
                    setNovaPastaEm(g.entity)
                    setNomeNovaPasta('')
                  }}
                >
                  <i className="fas fa-folder-plus" aria-hidden />
                </button>
                {onNovoCampo ? (
                  <button
                    type="button"
                    className="cat__grupo-add"
                    title={`Novo campo em ${g.titulo}`}
                    aria-label={`Novo campo em ${g.titulo}`}
                    onClick={() => onNovoCampo(g.entity, null)}
                  >
                    <i className="fas fa-plus" aria-hidden />
                  </button>
                ) : null}
                <span className="cat__grupo-n">{lista.length}</span>
              </div>
              {aberto ? (
                <>
                  {novaPastaEm === g.entity ? (
                    <div className="cat__nova-pasta">
                      <input
                        value={nomeNovaPasta}
                        autoFocus
                        placeholder="Nome da pasta"
                        onChange={(e) => setNomeNovaPasta(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') criarPasta(g.entity)
                          if (e.key === 'Escape') setNovaPastaEm(null)
                        }}
                      />
                      <button type="button" onClick={() => criarPasta(g.entity)}>
                        Criar
                      </button>
                      <button type="button" className="cat__nova-pasta-x" onClick={() => setNovaPastaEm(null)}>
                        Cancelar
                      </button>
                    </div>
                  ) : null}
                  {/* CAMPOS DE SISTEMA (so em Item de Trabalho): fixos, sem
                      lixeira; clique abre as instrucoes para editar */}
                  {g.entity === 'work_item' ? (
                    <div className="cat__pasta cat__pasta--sistema">
                      <div className="cat__pasta-cab">
                        <button
                          type="button"
                          className="cat__pasta-tog"
                          aria-expanded={busca.trim() !== '' || !recolhidos.has('sistema')}
                          onClick={() => alternar('sistema')}
                        >
                          <i
                            className={`fas fa-chevron-${busca.trim() !== '' || !recolhidos.has('sistema') ? 'down' : 'right'} cat__grupo-chev`}
                            aria-hidden
                          />
                          <i className="fas fa-lock cat__pasta-ico" aria-hidden />
                        </button>
                        <button type="button" className="cat__pasta-nome" onClick={() => alternar('sistema')}>
                          Campos do sistema
                        </button>
                        <span className="cat__grupo-n">{(sistema.data ?? []).length}</span>
                      </div>
                      {busca.trim() !== '' || !recolhidos.has('sistema') ? (
                        <div className="cat__lista cat__lista--pasta">
                          {(sistema.data ?? [])
                            .filter(
                              (f) =>
                                busca.trim() === '' || f.nome.toLowerCase().includes(busca.trim().toLowerCase()),
                            )
                            .map((f) => (
                              <div
                                key={f.key}
                                className="cat__campo cat__campo--sistema"
                                title="Campo do sistema — clique para ver e editar as instruções"
                                onClick={() => {
                                  setSistemaAberto(f.key)
                                  setSistemaEditando(false)
                                }}
                              >
                                <i className={`${f.icone} cat__grip`} aria-hidden />
                                <span className="cat__campo-nome">{f.nome}</span>
                                <span className="cat__campo-tipo">Sistema</span>
                              </div>
                            ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {daEntidade.map((p) =>
                    pasta(
                      p,
                      g.entity,
                      lista.filter((f) => f.catalogGroupId === p.id),
                    ),
                  )}
                  {soltos.length === 0 && daEntidade.length === 0 ? (
                    <p className="cat__vazio">{busca ? 'Nenhum campo.' : 'Nenhum campo ainda — use o + para criar.'}</p>
                  ) : (
                    <div className="cat__lista">{soltos.map(cartao)}</div>
                  )}
                </>
              ) : null}
            </div>
          )
        })}
      </div>

      {/* "Criar campo" no rodapé: abre os TIPOS em cards; ao escolher um, já vai
          para a tela de novo campo daquele tipo (pedido 2026-08-30) */}
      {onNovoCampoDoTipo ? (
        <div className={`ccf__criar${criarAberto ? ' ccf__criar--aberto' : ''}`}>
          <button
            type="button"
            className="ccf__criar-head"
            aria-expanded={criarAberto}
            onClick={() => setCriarAberto((v) => !v)}
          >
            <i className={`fas fa-chevron-${criarAberto ? 'down' : 'right'} ccf__criar-chev`} aria-hidden />
            <i className="fas fa-plus" aria-hidden />
            Criar campo
          </button>
          {sistemaAberto ? (
        (() => {
          const f = (sistema.data ?? []).find((x) => x.key === sistemaAberto)
          if (!f) return null
          return (
            <Modal
              open
              onClose={() => setSistemaAberto(null)}
              title={`Instruções — ${f.nome}`}
              width={640}
              resizable
              sizeKey="campo-info"
              headerExtra={
                // o lapis fica ao lado do X, como na modal do item
                !sistemaEditando ? (
                  <button
                    type="button"
                    className="cat__sistema-lapis"
                    title="Editar as instruções padrão deste campo"
                    aria-label="Editar as instruções padrão deste campo"
                    onClick={() => setSistemaEditando(true)}
                  >
                    <i className="fas fa-pen" aria-hidden />
                  </button>
                ) : null
              }
              footer={
                <>
                  <span className="cat__sistema-tag">
                    Campo do sistema — não pode ser apagado{f.editado ? ' · instruções editadas' : ''}
                  </span>
                  <span style={{ flex: 1 }} />
                  <Button variant="secondary" onClick={() => setSistemaAberto(null)}>
                    Fechar
                  </Button>
                </>
              }
            >
              {sistemaEditando ? (
                <RichTextEditor
                  value={f.help}
                  startEditing
                  minHeight={180}
                  placeholder="Explique como este campo deve ser usado…"
                  onSave={(html) => salvarSistema.mutate({ key: f.key, html })}
                  onEditingChange={(em) => {
                    if (!em) setSistemaEditando(false)
                  }}
                />
              ) : (
                // eslint-disable-next-line react/no-danger
                <div className="ob-rte__view" dangerouslySetInnerHTML={{ __html: f.help }} />
              )}
            </Modal>
          )
        })()
      ) : null}

      {criarAberto ? (
            <div className="ccf__tipos-grid ccf__tipos-grid--catalogo">
              {TIPOS_CAMPO.map((tp) => (
                <button
                  key={tp.value}
                  type="button"
                  className="ccf__tipocard"
                  title={tp.hint}
                  onClick={() => {
                    setCriarAberto(false)
                    onNovoCampoDoTipo(tp.value)
                  }}
                >
                  <i className={tp.icon} aria-hidden />
                  <span className="ccf__tipocard-nome">{tp.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </aside>
  )
}
